import {
  DayOfWeekMapping,
  DisplaySchema,
  DisplayType,
  FilterParameter,
  nameof,
  ObjectSchema,
  ViewConfig,
  ViewType
} from '@summit/models';

import { MultiCompanyLogicMapping } from '../payroll/multi-company-logic.enum';
import { PerDiemMethodMapping } from '../payroll/per-diem/per-diem-method.enum';
import { ScheduleStatusMapping } from '../schedule/schedule-status.enum';
import { RoundingMethodMapping } from '../shared/rounding-method.enum';
import { TruckColumnsMapping } from '../shared/truck-columns.enum';
import { UserExperience } from '../shared/user-experience';
import { TenantScheduleParameter } from '../tenant-schedule-parameter';
import { TruckTypeMapping } from '../truck/truck-type.enum';
import { FinancialSalaryDistributionMapping } from './financial-salary-distribution.enum';
import { LogEntryMethodMapping } from './log-entry-method.enum';
import { PayrollBreakdownMapping } from './payroll-breakdown.enum';
import { PayrollFormatMapping } from './payroll-format.enum';
import { PendingImplementationMapping } from './pending-implementation.enum';
import { Tenant } from './tenant';
import { TenantPayrollParameter } from './tenant-payroll-parameter';
import { TenantReconcileProcessParameter } from './tenant-reconcile-process-parameter';
import { TenantStatusFlags } from './tenant-status-flags.enum';

export class TenantSchema extends DisplaySchema<Tenant> {
  readonly refTitle = 'Tenants';
  override refServerAPI = 'Tenants';
  override readonly refServerObjectName = 'TemplateObject';
  override readonly clientOnlyFields = [
    'state',
    'hidden',
    'overrideReconcileLock',
    'earlyPayrollWeek',
    'credentials.tenant',
    'credentials.synchronize',
    'credentials.hub',
    'credentials.company',
    'credentials.autoImport',
    'credentials.allowDelete',
    'credentials.modifiedBy',
    'systemEmployee',
    'disabled',
    'defaultCompany',
    nameof<Tenant>('currentSubscription')
  ];

  tenantStatusFlags = TenantStatusFlags;

  constructor(ux: UserExperience) {
    super();
    if (ux?.isSysAdmin) {
      //this.HideColumns('Normal', [nameof<Tenant>('testingOffset')]);
      this.refColumnList['testingOffset'].columnField = true;
    }
  }
  override rowClassRules = {
    'ebo-feature-disabled': (params) => {
      return (params.data?.status & TenantStatusFlags.Hidden) > 0;
    }
  };

  readonly refColumnList: ObjectSchema<Tenant> = {
    id: { propertyName: nameof<Tenant>('id'), formField: true },
    name: {
      propertyName: nameof<Tenant>('name'),
      formField: true,
      columnField: true,
      displayName: 'Company / Entity Name',
      validationMessages: [{ type: 'required', message: 'Name is required' }],
      routerLink: ['/tenants', 'this.id']
    },
    abbreviation: {
      propertyName: nameof<Tenant>('abbreviation'),
      formField: true,
      columnField: true,
      displayName: 'Abbreviation',
      validationMessages: [{ type: 'required', message: 'Abbreviation is required' }]
    },
    endOfWeek: {
      propertyName: nameof<Tenant>('endOfWeek'),
      formField: true,
      columnField: true,
      displayName: 'End Of Week',
      displayType: DisplayType.Enum,
      displayTypeParameter: DayOfWeekMapping,
      displayHint: 'End of week for payroll',
      validationMessages: [{ type: 'required', message: 'End of Week is required' }]
    },
    endOfScheduleWeek: {
      propertyName: nameof<Tenant>('endOfScheduleWeek'),
      formField: true,
      columnField: true,
      displayName: 'End Of Schedule Week',
      displayType: DisplayType.Enum,
      displayTypeParameter: DayOfWeekMapping,
      displayHint: 'End of week for Scheduling',
      validationMessages: [{ type: 'required', message: 'End of Schedule Week is required' }]
    },
    endOfIvmrWeek: {
      propertyName: nameof<Tenant>('endOfIvmrWeek'),
      formField: true,
      columnField: true,
      displayName: 'End Of IVMR Week',
      displayType: DisplayType.Enum,
      displayTypeParameter: DayOfWeekMapping,
      displayHint: 'End of week for IVMRs',
      validationMessages: [{ type: 'required', message: 'End of IVMR Week is required' }]
    },
    stateID: {
      propertyName: nameof<Tenant>('stateID'),
      formField: true,
      displayName: 'Operating State',
      validationMessages: [{ type: 'required', message: 'State is required' }]
    },
    state: {
      propertyName: nameof<Tenant>('state'),
      columnField: true,
      displayName: 'Operating State',
      displayType: DisplayType.Name
    },
    defaultTruckType: {
      propertyName: nameof<Tenant>('defaultTruckType'),
      formField: true,
      //columnField: true,
      displayName: 'Default Truck Type',
      displayType: DisplayType.Enum,
      displayTypeParameter: TruckTypeMapping
    },
    defaultTruckTypeFormatted: {
      propertyName: nameof<Tenant>('defaultTruckTypeFormatted'),
      //formField: true,
      columnField: true,
      displayName: 'Default Truck Type',
      displayType: DisplayType.FormattedText,
      displayTypeParameter: TruckTypeMapping
    },
    status: {
      propertyName: nameof<Tenant>('status'),
      formField: true,
      //columnField: true,
      //displayType: DisplayType.statusName,
      displayName: 'Active Status',
      template: true
    },
    statusFormatted: {
      propertyName: nameof<Tenant>('statusFormatted'),
      //formField: true,
      columnField: true,
      displayType: DisplayType.FormattedText,
      displayName: 'Active Status',
      //template: true,
      cellClassRules: {
        highlight: (params) => {
          if (
            this.tenantStatusFlags.EarlyPayrollWeek ===
            (params.data.status & this.tenantStatusFlags.EarlyPayrollWeek)
          )
            return true;
          return false;
        },
        'alert-danger': (params) => {
          if (
            this.tenantStatusFlags.OverrideReconcileLock ===
            (params.data.status & this.tenantStatusFlags.OverrideReconcileLock)
          )
            return true;
          return false;
        }
      }
    },
    systemEmployeeID: {
      propertyName: nameof<Tenant>('systemEmployeeID'),
      formField: true,
      displayName: 'System Account',
      displayHint: `Account used as the "Do-er" of Automated Tasks.`
    },
    systemEmployee: {
      propertyName: nameof<Tenant>('systemEmployee'),
      columnField: true,
      displayName: 'System Account',
      displayType: DisplayType.ShortString
    },
    defaultCompanyID: {
      propertyName: nameof<Tenant>('defaultCompanyID'),
      formField: true,
      displayName: 'Default Company'
    },
    defaultCompany: {
      propertyName: nameof<Tenant>('defaultCompany'),
      columnField: true,
      displayName: 'Default Company',
      displayType: DisplayType.ShortString
    },
    hidden: {
      propertyName: 'hidden',
      formField: true,
      displayName: 'Hidden/Off-Boarding'
    },
    overrideReconcileLock: {
      propertyName: 'overrideReconcileLock',
      formField: true,
      displayName: 'Override Reconcile Lock'
    },
    earlyPayrollWeek: {
      propertyName: 'earlyPayrollWeek',
      formField: true,
      displayName: 'Early Payroll Week'
    },
    credentials: {
      propertyName: nameof<Tenant>('credentials'),
      formField: true,
      columnField: true,
      displayName: 'Credentials',
      displayType: DisplayType.ShortStringArray,
      displayClass: 'preserve-format'
    },
    currentCredentials: {
      propertyName: nameof<Tenant>('currentCredentials'),
      columnField: true,
      displayName: 'Credentials (Current)',
      displayType: DisplayType.ShortStringArray,
      displayClass: 'preserve-format'
    },
    credentialTypes: {
      propertyName: nameof<Tenant>('credentialTypes'),
      columnField: true,
      displayName: 'Credential Types'
    },
    finance: {
      propertyName: nameof<Tenant>('finance'),
      columnField: true,
      displayName: 'Finance Parameters',
      children: {
        reportsTruckSort: {
          propertyName: 'reportsTruckSort',
          formField: true,
          displayName: 'Truck Sort By',
          displayType: DisplayType.Enum,
          displayTypeParameter: TruckColumnsMapping
        },
        salariesAboveTheLine: {
          propertyName: 'salariesAboveTheLine',
          formField: true,
          displayName: `Salary is 'Above The Line'`,
          displayType: DisplayType.Boolean
        },
        salaryDistribution: {
          propertyName: 'salaryDistribution',
          formField: true,
          displayName: 'Distribute Salary',
          displayHint: 'How managers salary distributed for profitability reporting',
          displayType: DisplayType.Enum,
          displayTypeParameter: FinancialSalaryDistributionMapping
        }
      }
    },
    payroll: {
      propertyName: nameof<Tenant>('payroll'),
      columnField: true,
      displayName: 'Payroll Parameters',
      children: {
        pendingImplementation: {
          propertyName: nameof<TenantPayrollParameter>('pendingImplementation'),
          formField: true,
          displayName: 'Pending Legs',
          displayType: DisplayType.Enum,
          displayTypeParameter: PendingImplementationMapping,
          displayHint: 'How do you pay Pending Legs?'
        },
        perDiemMethod: {
          propertyName: nameof<TenantPayrollParameter>('perDiemMethod'),
          formField: true,
          displayName: 'Per Diem Calc. Method',
          displayType: DisplayType.Enum,
          displayTypeParameter: PerDiemMethodMapping,
          displayHint: 'How do you count Per Diem Days?'
        },
        hosHoursPerPerDiemDay: {
          propertyName: nameof<TenantPayrollParameter>('hosHoursPerPerDiemDay'),
          formField: true,
          displayName: 'HOS Hours in shift for Per Diem',
          displayType: DisplayType.Number,
          displayHint: 'Multiple of 3, Default: 24'
        },

        multiCompanyLogic: {
          propertyName: nameof<TenantPayrollParameter>('multiCompanyLogic'),
          formField: true,
          displayName: 'Multi-Company Logic',
          displayType: DisplayType.Enum,
          displayTypeParameter: MultiCompanyLogicMapping,
          displayHint: 'How do you split pay across companies?'
        },
        autoPaySettlementJumps: {
          propertyName: 'autoPaySettlementJumps',
          formField: true,
          displayName: 'Auto Pay Settlement Jumps',
          displayType: DisplayType.Boolean,
          displayHint: `Automatically Pay Settlement Jumps.`
        },
        perDiemIncremental: {
          propertyName: 'perDiemIncremental',
          formField: true,
          displayName: 'Per Diem is Additional',
          displayType: DisplayType.Boolean,
          displayHint: `Per Diem is in 'Addition To' instead of 'Part Of' Wage.`
        },
        integrateSpotMiles: {
          propertyName: 'integrateSpotMiles',
          formField: true,
          displayName: 'Integrate Spot Miles',
          displayType: DisplayType.Boolean,
          displayHint: 'Integrate Spot Miles as Solo Miles for Payroll report.'
        },
        allowGuaranteesToBeCorrectable: {
          propertyName: 'allowGuaranteesToBeCorrectable',
          formField: true,
          displayName: 'Allow Guarantees to be Correctable',
          displayType: DisplayType.Boolean,
          displayHint: 'Possible Overpayment, leave disabled unless you specifically need this.'
        },
        errorOnNoSpotWage: {
          propertyName: nameof<TenantPayrollParameter>('errorOnNoSpotWage'),
          formField: true,
          displayName: 'Error on No Spot Wage',
          displayType: DisplayType.Boolean,
          displayHint: 'During Payroll Validation, Error when there is No Spot Wage.'
        },

        format: {
          propertyName: 'format',
          formField: true,
          displayName: 'Payroll Format',
          displayType: DisplayType.Enum,
          displayTypeParameter: PayrollFormatMapping
        },
        templateName: {
          propertyName: 'templateName',
          formField: true,
          displayName: 'Template Name (System Use)'
        },
        includeProcessFileWithDraft: {
          propertyName: 'includeProcessFileWithDraft',
          formField: true,
          displayName: 'Include Export with Draft',
          displayType: DisplayType.Boolean
        },
        breakdown: {
          propertyName: 'breakdown',
          formField: true,
          displayName: 'Payroll Export Breakdown',
          displayType: DisplayType.Enum,
          displayTypeParameter: PayrollBreakdownMapping
        }
      }
    },
    reconcileProcess: {
      propertyName: nameof<Tenant>('reconcileProcess'),
      columnField: true,
      displayName: 'Reconcile Process Parameters',
      children: {
        entryMethod: {
          propertyName: nameof<TenantReconcileProcessParameter>('entryMethod'),
          formField: true,
          displayName: 'Linehaul Entry Method',
          displayType: DisplayType.Enum,
          displayTypeParameter: LogEntryMethodMapping,
          displayHint: 'How are the Logs being created? (None = Auto-Dispatch Day)'
        },
        autoMatchMismatchedDrivers: {
          propertyName: nameof<TenantReconcileProcessParameter>('autoMatchMismatchedDrivers'),
          formField: true,
          displayName: 'Auto Match: FedEx entry of incorrect driver.',
          displayType: DisplayType.Boolean
        },
        autoMarkPending: {
          propertyName: 'autoMarkPending',
          formField: true,
          displayName: 'Auto Mark: Pending Week Ending Legs to bring Driver Home.',
          displayType: DisplayType.Boolean
        },
        autoMoveToDay: {
          propertyName: 'autoMoveToDay',
          formField: true,
          displayName: 'Auto Move to Est. Dispatch Day',
          displayType: DisplayType.Boolean
        },
        autoCorrectPendingMiles: {
          propertyName: 'autoCorrectPendingMiles',
          formField: true,
          displayName: 'Auto Correct: Pending Miles.',
          displayType: DisplayType.Boolean
        },
        autoRemoveCircularJumps: {
          propertyName: 'autoRemoveCircularJumps',
          formField: true,
          displayName: 'Auto Remove: Circular Jumps (Same Day).',
          displayType: DisplayType.Boolean,
          displayHint: 'Enable to remove circular jumps (A-B-C-A) within the same day.'
        },
        createZeroMileReturnsForDayWages: {
          propertyName: 'createZeroMileReturnsForDayWages',
          formField: true,
          displayName: 'Auto Create: 0 Mile Return leg for Day Pay / Per Diem Drivers.',
          displayType: DisplayType.Boolean
        },
        returnsForDayMinimumMiles: {
          propertyName: 'returnsForDayMinimumMiles',
          formField: true,
          displayName: 'Minimum Miles for Return Leg.',
          displayType: DisplayType.Miles
        },
        passRerouteOverpayToDriver: {
          propertyName: nameof<TenantReconcileProcessParameter>('passRerouteOverpayToDriver'),
          formField: true,
          displayName: 'Pass ReRoute Overpay to Driver.',
          displayHint: 'On re-route, Driver asks for 300 miles, FedEx pays 400 miles, Driver gets Full 400 miles.',
          displayType: DisplayType.Boolean
        },
        statusToCreateDailyLogsFor: {
          propertyName: nameof<TenantReconcileProcessParameter>('statusToCreateDailyLogsFor'),
          formField: true,
          displayName: 'Create For',
          displayType: DisplayType.EnumArray,
          displayTypeParameter: ScheduleStatusMapping,
          displayHint: 'Defaults to All Running Statuses'
        },
        includeSettlementExcel: {
          propertyName: 'includeSettlementExcel',
          formField: true,
          displayName: 'Include Excel in Settlement download',
          displayType: DisplayType.Boolean
        }
      }
    },
    scheduleConfig: {
      propertyName: nameof<Tenant>('scheduleConfig'),
      columnField: true,
      displayName: 'Schedule Configuration',
      children: {
        passThroughOnDispatch: {
          propertyName: nameof<TenantScheduleParameter>('passThroughOnDispatch'),
          formField: true,
          displayName: 'Passthrough To FedEx',
          displayType: DisplayType.EnumArray,
          displayTypeParameter: ScheduleStatusMapping,
          displayHint: 'Instead of TBD. Defaults to Declined and Weather Declined.'
        }
      }
    },
    //Form Only Fields
    featureChange: {
      propertyName: 'featureChange',
      children: {
        requestFeatureChange: {
          propertyName: 'requestFeatureChange',
          formField: true,
          displayName: 'Change Subscription'
        },
        driverLogs: {
          propertyName: 'driverLogs',
          formField: true,
          displayName: 'Driver Logs'
        },
        scheduler: {
          propertyName: 'scheduler',
          formField: true,
          displayName: 'Scheduler'
        },
        payroll: {
          propertyName: 'payroll',
          formField: true,
          displayName: 'Payroll'
        },
        maintBasic: {
          propertyName: 'maintBasic',
          formField: true,
          displayName: 'Maint. Basic'
        }
      }
    },
    //Column Fields for Current Subscription
    currentInvoiceCycleDate: {
      propertyName: nameof<Tenant>('currentInvoiceCycleDate'),
      columnField: true,
      displayName: 'Bill Date',
      displayType: DisplayType.Number
    },
    currentPricePerTruck: {
      propertyName: nameof<Tenant>('currentPricePerTruck'),
      columnField: true,
      displayName: '$/Truck',
      displayType: DisplayType.Money
    },
    currentDiscount: {
      propertyName: nameof<Tenant>('currentDiscount'),
      columnField: true,
      displayName: 'Discount',
      displayType: DisplayType.PercentageFromWholeNumber
    },
    currentTruckOverride: {
      propertyName: nameof<Tenant>('currentTruckOverride'),
      columnField: true,
      displayName: 'Truck Override',
      displayType: DisplayType.Number
    },
    currentFeatures: {
      propertyName: nameof<Tenant>('currentFeatures'),
      columnField: true,
      displayName: 'Features',
      displayType: DisplayType.ShortString,
      displayClass: 'preserve-format'
    },
    featureSummary: {
      propertyName: nameof<Tenant>('featureSummary'),
      columnField: true,
      displayName: 'Feature Summary'
    },
    currentSubComment: {
      propertyName: nameof<Tenant>('currentSubComment'),
      columnField: true,
      displayName: 'Comment'
    },
    testingOffset: {
      propertyName: nameof<Tenant>('testingOffset'),
      formField: true,
      columnField: false, //changed in constructor
      displayName: 'Import Offset',
      displayType: DisplayType.Number
    },
    reconcileOrder: {
      propertyName: nameof<Tenant>('reconcileOrder'),
      formField: true,
      columnField: true,
      displayName: 'Reconcile Order',
      displayType: DisplayType.Number,
      displayHint: 'Default 1000, Lower numbers are first in the list.',
      displayNullText: '1000'
    },
    reconcileNotes: {
      propertyName: nameof<Tenant>('reconcileNotes'),
      formField: true,
      columnField: true,
      displayName: 'Reconcile Notes',
      displayType: DisplayType.HTML
    },
    reconciler: {
      propertyName: nameof<Tenant>('reconciler'),
      formField: true,
      columnField: true,
      displayName: 'Reconciler'
    },
    hasFedExAccount: {
      propertyName: nameof<Tenant>('hasFedExAccount'),
      columnField: true,
      displayName: 'FedEx Active?',
      displayType: DisplayType.Boolean
    },
    ivmrEmailSkipTerminal: {
      propertyName: nameof<Tenant>('ivmrEmailSkipTerminal'),
      formField: true,
      columnField: true,
      displayName: 'IVMR Email Skip Terminal',
      displayType: DisplayType.Boolean
    },
    inboxAddress: {
      propertyName: nameof<Tenant>('inboxAddress'),
      formField: true,
      columnField: true,
      displayName: 'Inbox Address'
    },
    ivmrAutoCreate: {
      propertyName: nameof<Tenant>('ivmrAutoCreate'),
      formField: true,
      columnField: true,
      displayName: 'IVMR Auto Create',
      displayType: DisplayType.Boolean
    },
    ivmrRoundingMethod: {
      propertyName: nameof<Tenant>('ivmrRoundingMethod'),
      formField: true,
      columnField: true,
      displayName: 'IVMR Rounding Method',
      displayType: DisplayType.Enum,
      displayTypeParameter: RoundingMethodMapping
    },
    odometerDaysOfHistory: {
      propertyName: nameof<Tenant>('odometerDaysOfHistory'),
      formField: true,
      columnField: true,
      displayName: 'Odometer Days of History',
      displayType: DisplayType.Number,
      displayHint: 'Number of days to search for last reading (Effects Query Time, Default 180)'
    },
    maintOdometerSyncWithIvmr: {
      propertyName: nameof<Tenant>('maintOdometerSyncWithIvmr'),
      formField: true,
      columnField: true,
      displayName: 'Sync with IVMR Offsets',
      displayType: DisplayType.Boolean
    },
    regulatoryThresholdFlag: {
      propertyName: nameof<Tenant>('regulatoryThresholdFlag'),
      formField: true,
      columnField: true,
      displayName: 'Regulatory Threshold Flag',
      displayType: DisplayType.Number,
      displayHint: 'Defaults to 90 days before CDL & Medical Expiration'
    },
    regulatoryThresholdWarn: {
      propertyName: nameof<Tenant>('regulatoryThresholdWarn'),
      formField: true,
      columnField: true,
      displayName: 'Regulatory Threshold Warn',
      displayType: DisplayType.Number,
      displayHint: 'Defaults to 60 days before CDL & Medical Expiration'
    },
    regulatoryThresholdAlarm: {
      propertyName: nameof<Tenant>('regulatoryThresholdAlarm'),
      formField: true,
      columnField: true,
      displayName: 'Regulatory Threshold Alarm',
      displayType: DisplayType.Number,
      displayHint: 'Defaults to 30 days before CDL & Medical Expiration'
    }
  };

  override readonly refFilterParameters: FilterParameter[] = [
    new FilterParameter({
      id: 'activeOnly',
      //description: 'Hide Disabled',
      state: false,
      filterData: true
    })
  ];

  readonly refViewModels: ViewConfig<Tenant>[] = [
    {
      id: 'Normal',
      name: 'Tenants',
      type: ViewType.Standard,
      showNewButton: true,
      selectable: 'multiple',
      viewColumns: [
        'name',
        'abbreviation',
        'endOfWeek',
        'state',
        'defaultTruckTypeFormatted',
        'statusFormatted',
        'systemEmployee',
        'currentCredentials'
      ],
      sortParameters: { id: 'name', start: 'asc' },
      filterParameters: [
        {
          id: 'activeOnly',
          state: false
        }
      ]
    },
    {
      id: 'CheckList',
      name: 'Check List',
      type: ViewType.Standard,
      showNewButton: true,
      selectable: 'multiple',
      viewColumns: ['reconcileOrder', 'abbreviation', 'hasFedExAccount', 'reconcileNotes'],
      groupBy: ['reconciler'],
      sortParameters: [
        { id: 'reconcileOrder', start: 'asc' },
        { id: 'abbreviation', start: 'asc' }
      ],
      filterString: 'FedEx',
      filterParameters: [
        {
          id: 'activeOnly',
          state: true
        }
      ]
      // filterString: '303',
    },
    {
      id: 'Subscriptions',
      name: 'Subscriptions',
      type: ViewType.Standard,
      showNewButton: true,
      viewColumns: [
        'name',
        'abbreviation',
        'currentInvoiceCycleDate',
        'currentPricePerTruck',
        'currentDiscount',
        'currentTruckOverride',
        'currentFeatures',
        'currentSubComment'
      ],
      sortParameters: { id: 'name', start: 'asc' }
      // filterString: '303',
    }
  ];

  NewObject(jsonData?: Partial<Tenant>): Tenant {
    return new Tenant(jsonData);
  }
}
